/*
  TYOHAAR search panel.

  This does NOT search. Every result comes from Shopify's Predictive Search
  endpoint, asked for as a rendered section:

    /search/suggest?q=<term>&section_id=predictive-search-tyohaar

  Shopify decides what matches, in what order, which suggestions to offer and
  which part of each to embolden. This file only opens the panel, debounces
  typing, drops the returned markup in, and handles the keyboard.

  Dawn's own <predictive-search> is untouched and still works; this is a
  separate element that happens to call the same endpoint.
*/
class TyohaarSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-tys-input]');
    this.panel = this.querySelector('[data-tys-panel]');
    this.results = this.querySelector('[data-tys-results-slot]');
    this.openButton = this.querySelector('[data-tys-open]');
    this.closeButton = this.querySelector('[data-tys-close]');
    this.scrim = this.querySelector('[data-tys-scrim]');

    if (!this.input || !this.panel) return;

    this.sectionId = this.dataset.sectionId || 'predictive-search-tyohaar';
    this.cache = new Map();
    this.abort = null;
    this.timer = null;

    this.openButton?.addEventListener('click', () => this.open());
    this.closeButton?.addEventListener('click', () => this.close());

    /*
      The scrim needs its own handler. The outside-click listener below asks
      whether the click landed inside this element, and the scrim is inside
      it — so clicking the dimmed page would otherwise do nothing, which is
      the one thing a dimmed page is supposed to respond to.
    */
    this.scrim?.addEventListener('click', () => this.close());

    this.input.addEventListener('input', () => this.onInput());
    this.addEventListener('keydown', (event) => this.onKeydown(event));

    // Closing on an outside click is bound to the document, so it has to come
    // off again if this element is ever removed.
    this.onDocumentClick = (event) => {
      if (this.hasAttribute('open') && !this.contains(event.target)) this.close();
    };
    document.addEventListener('click', this.onDocumentClick);

    // The bar moves when the window does, so the panel has to be re-measured.
    this.onResize = () => { if (this.hasAttribute('open')) this.position(); };
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('resize', this.onResize);
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    clearTimeout(this.timer);
    clearTimeout(this.closeTimer);
    this.abort?.abort();
  }

  /*
    Where the panel hangs.

    Anchoring it in CSS to the trigger put it fourteen pixels under the WORD
    "Search" — which is halfway up a hundred-pixel header, so it opened on top
    of the bar it belongs under. The header's height is a setting and its
    padding is fluid, so there is no number to hard-code here; the only honest
    source is the bar itself, measured when the panel opens.
  */
  position() {
    const bar = this.closest('.tyoh')?.querySelector('.tyoh__bar');
    if (!bar) return;

    const rect = bar.getBoundingClientRect();
    this.style.setProperty('--tys-panel-top', `${Math.round(rect.bottom + 8)}px`);
    // The scrim starts flush with the bar, with none of the panel's 8px gap:
    // a lit stripe between the header and the dimming would read as a seam.
    this.style.setProperty('--tys-scrim-top', `${Math.round(rect.bottom)}px`);
  }

  /*
    THE PAGE IS NO LONGER FROZEN, AND IT CANNOT BE.

    This used to put overflow:hidden on html and body while the panel was
    open. That holds the page still, but it also takes the header with it:
    the header is position:sticky, and sticky only works for as long as its
    scroll container can actually scroll. Freeze the document and every
    sticky element drops to its static position — which, once the visitor has
    scrolled at all, is somewhere above the top of the screen. Open the search
    and the whole bar vanished.

    So the page stays scrollable. The panel is position:fixed, so it holds its
    place regardless, and the results list carries overscroll-behavior so a
    scroll inside it does not run on into the page behind. Scrolling the page
    while the panel is open is a good deal less strange than the header
    disappearing.

    Kept as a method rather than deleted at the call sites: whether the page
    locks is this component's business, and a no-op here says that on purpose.
  */
  lockScroll() {
    /* deliberately does nothing — see above */
  }

  /*
    How long the closing fade lasts, read from the same custom property the
    stylesheet animates on, so the setting in the theme editor moves both and
    they cannot drift apart. Reduced motion skips the wait entirely.
  */
  outDuration() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;

    const raw = getComputedStyle(this.panel).getPropertyValue('--tys-out').trim();
    const value = parseFloat(raw);
    if (!Number.isFinite(value)) return 130;

    // Authored as ms today, but s is legal CSS and would otherwise be read as
    // a fraction of a millisecond.
    return raw.endsWith('ms') ? value : value * 1000;
  }

  open() {
    // Typing calls this through render(), and the trigger can be clicked
    // while the panel is already up. Re-running the entrance would restart
    // the animation from off-screen under the visitor's cursor.
    if (this.hasAttribute('open')) return;

    // A close still counting down would hide the panel we are about to show.
    clearTimeout(this.closeTimer);
    this.panel.classList.remove('is-closing');

    this.setAttribute('open', '');
    this.panel.hidden = false;
    if (this.scrim) this.scrim.hidden = false;
    this.openButton?.setAttribute('aria-expanded', 'true');
    this.position();

    /*
      Two frames, not one.

      hidden going away and .is-open arriving in the same frame is a single
      style resolution: the browser has no earlier value to interpolate from
      and the panel simply lands, finished, exactly the pop this replaced.
      The first frame lets display settle, the second starts the transition
      from a state that has actually been rendered.
    */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.panel.classList.add('is-open');
        this.scrim?.classList.add('is-open');
      });
    });
    /*
      The page can scroll while this is open now, and the bar it hangs from
      is sticky — so where the bar ends changes as the announcement strip
      above it scrolls away. Measured once at open, the panel would drift off
      the header. Only while open, and passive, because this only reads.
      (Resize is already bound for the lifetime of the element.)
    */
    this.onScroll = this.onScroll || (() => this.position());
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.lockScroll(true);
    // The panel is useless without focus in the field, and a phone keyboard
    // should come up straight away.
    this.input.focus();
  }

  close() {
    if (!this.hasAttribute('open')) return;

    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    this.removeAttribute('open');
    this.openButton?.setAttribute('aria-expanded', 'false');

    this.panel.classList.remove('is-open');
    this.panel.classList.add('is-closing');
    this.scrim?.classList.remove('is-open');

    /*
      hidden goes back on only once the fade has finished, or the panel would
      vanish mid-transition and there would be nothing to watch.

      A timer rather than transitionend: that event never fires if the panel
      is scrolled out of view, if the tab is in the background, or if the
      duration is zero — and a panel left interactive forever is a worse
      failure than one that hides a frame late.
    */
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      this.panel.hidden = true;
      this.panel.classList.remove('is-closing');
      if (this.scrim) this.scrim.hidden = true;
    }, this.outDuration());

    this.lockScroll(false);
  }


  onInput() {
    const term = this.input.value.trim();

    // Below two characters Shopify has almost nothing to go on and every
    // keystroke would be a wasted request.
    if (term.length < 2) {
      this.results.innerHTML = '';
      return;
    }

    // One request per pause, not one per key. The previous one is abandoned so
    // a slow early answer cannot arrive after a fast later one and overwrite it.
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.fetchResults(term), 250);
  }

  async fetchResults(term) {
    if (this.cache.has(term)) {
      this.render(this.cache.get(term));
      return;
    }

    this.abort?.abort();
    this.abort = new AbortController();
    this.setAttribute('loading', '');

    const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(term)}&section_id=${this.sectionId}`;

    try {
      const response = await fetch(url, { signal: this.abort.signal });
      if (!response.ok) throw new Error(response.status);

      const text = await response.text();
      const markup = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector(`#shopify-section-${this.sectionId}`)?.innerHTML;

      if (markup == null) throw new Error('no section in response');

      this.cache.set(term, markup);
      this.render(markup);
    } catch (error) {
      if (error.name === 'AbortError') return;
      // A failed suggestion is not worth breaking the page over: the field
      // still submits to the full search page, which is the real destination.
      this.results.innerHTML = '';
    } finally {
      this.removeAttribute('loading');
    }
  }

  render(markup) {
    this.results.innerHTML = markup;
    if (!this.hasAttribute('open')) this.open();
  }

  get options() {
    return Array.from(this.querySelectorAll('[role="option"] a'));
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      this.openButton?.focus();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const options = this.options;
    if (!options.length) return;

    event.preventDefault();
    const current = options.indexOf(document.activeElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    // Wraps at both ends, so holding an arrow never dead-ends in a corner.
    const next = (current + step + options.length) % options.length;
    options[next].focus();
  }
}

customElements.define('tyohaar-search', TyohaarSearch);
